import React, { lazy, Suspense, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import SEO from '../components/SEO/SEO'
import Header from '../components/organisms/Header/Header'
const Footer = lazy(() => import(/* webpackChunkName: "Footer" */ '../components/organisms/Footer'))
import TextSection from '../components/molecules/TextSection/TextSection'
import styles from './Contact.module.css'

/**
 * Contact - Page de contact avec informations pratiques
 * @page
 */
const Contact = () => {
  const { t } = useTranslation('contact')
  const navigate = useNavigate()
  const observerRef = useRef(null)

  useEffect(() => {
    // Configuration de l'Intersection Observer
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible)
        } else {
          entry.target.classList.remove(styles.visible)
        }
      })
    }, observerOptions)

    // Observer tous les éléments avec la classe animate
    const animatedElements = document.querySelectorAll(`.${styles.animate}`)
    animatedElements.forEach((el) => observerRef.current.observe(el))

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return (
    <div className={styles.page}>
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        url="/contact"
        image="/contact/club-dos.jpeg"
      />
      <Header />
      <main className={styles.main}>
        <div className={styles.hero}>
          <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
          <p className={styles.heroSubtitle}>
            {t('hero.subtitle')}
          </p>
        </div>

        <div className={styles.content}>
          <div className={`${styles.infoGrid} ${styles.animate}`}>
            {/* Lieu d'entraînement */}
            <div className={styles.infoCard}>
              <div className={styles.iconWrapper}>
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('location.title')}</h2>
              <p className={styles.cardText}>
                <strong>{t('location.venue')}</strong>
                <br />
                {t('location.address')}
                <br />
                {t('location.city')}
              </p>
              <a
                href="https://www.google.com/maps/place/79+Rue+Fernand+Soors,+33140+Villenave-d'Ornon"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.cardLink}
              >
                {t('location.mapLink')}
              </a>
            </div>

            {/* Horaires */}
            <div className={styles.infoCard}>
              <div className={styles.iconWrapper}>
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('schedule.title')}</h2>
              <p className={styles.cardText}>
                <strong>{t('schedule.day')} {t('schedule.time')}</strong>
              </p>
              <p className={styles.cardText}>
                <strong>{t('schedule.day2')} {t('schedule.time2')}</strong>
              </p>
              <p className={styles.cardNote}>
                {t('schedule.note1')}
                <br />
                {t('schedule.note2')}
              </p>
            </div>

            {/* Email */}
            <div className={styles.infoCard}>
              <div className={styles.iconWrapper}>
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('email.title')}</h2>
              <a
                href="mailto:burdigalabaggers@gmail.com"
                className={styles.cardLink}
              >
                {t('email.address')}
              </a>
              <p className={styles.cardNote}>
                {t('email.note')}
              </p>
            </div>

            {/* Téléphone */}
            <div className={styles.infoCard}>
              <div className={styles.iconWrapper}>
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('phone.title')}</h2>
              <a href="tel:+33743573735" className={styles.cardLink}>
                {t('phone.number')}
              </a>
              <p className={styles.cardNote}>
                {t('phone.note')}
              </p>
            </div>

            {/* Inscription */}
            <div
              className={`${styles.infoCard} ${styles.clickableCard}`}
              onClick={() => navigate('/adhesion')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate('/adhesion')
                }
              }}
            >
              <div className={styles.iconWrapper}>
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('membership.title')}</h2>
              <p className={styles.cardText}>
                {t('membership.text')}
              </p>
              <span className={styles.cardLink}>
                {t('membership.link')}
              </span>
            </div>
          </div>

          <TextSection>
            <div className={`${styles.ctaSection} ${styles.animate}`}>
              <h2 className={styles.ctaTitle}>
                {t('cta.title')}
              </h2>
              <p className={styles.ctaText}>
                {t('cta.text')}
              </p>
              <div className={styles.ctaButtons}>
                <a
                  href="https://www.google.com/maps/place/79+Rue+Fernand+Soors,+33140+Villenave-d'Ornon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.primaryButton}
                >
                  {t('cta.directionsButton')}
                </a>
                <a
                  href="mailto:burdigalabaggers@gmail.com"
                  className={styles.secondaryButton}
                >
                  {t('cta.contactButton')}
                </a>
              </div>
            </div>
          </TextSection>
        </div>
      </main>
      <Suspense fallback={<div style={{ height: '200px' }} />}>
        <Footer />
      </Suspense>
    </div>
  )
}

export default Contact
